import { useCallback } from "react";
import { Link, useLocation } from 'react-router-dom';
import { prefetchRoute } from "@/lib/prefetch-routes";
import { MenuConfig } from "@/config/types";
import { normalizePathname } from "@/lib/normalizePathname";
import {
  AccordionMenu,
  AccordionMenuIndicator,
  AccordionMenuSub,
  AccordionMenuSubTrigger,
  AccordionMenuSubContent,
  AccordionMenuItem,
} from '@marine/ui/accordion-menu';
import { Badge } from '@marine/ui/badge';
import { Minus, Plus } from "lucide-react";

interface SidebarMenuSectionProps {
  /** Menu configuration from layout.config.tsx */
  config: MenuConfig;
  /** Unique identifier for the accordion section (used for collapsing) */
  sectionId: string;
  /** Whether this section is expanded by default */
  defaultExpanded?: boolean;
}

/**
 * Shared accordion menu section component.
 * Reduces boilerplate by extracting common logic from sidebar menu components.
 * 
 * Usage:
 * ```tsx
 * <SidebarMenuSection 
 *   config={MENU_TENANT_LEADS} 
 *   sectionId="leads" 
 *   defaultExpanded 
 * />
 * ```
 */
export function SidebarMenuSection({ 
  config, 
  sectionId, 
  defaultExpanded = true 
}: SidebarMenuSectionProps) {
  const { pathname } = useLocation();

  // Compare with trailing slashes normalized so a `/crm/` location still
  // matches the `/crm` menu entry.
  const normalizedPathname = normalizePathname(pathname);
  const matchPath = useCallback(
    (path: string): boolean => normalizePathname(path) === normalizedPathname,
    [normalizedPathname],
  );

  const triggerValue = `${sectionId}-trigger`;

  return (
    <AccordionMenu
      // #908 — AccordionMenu is controlled: `defaultValue` is ignored and the
      // section opens because `selectedValue` seeds the initial active chain.
      // Omit it for collapsed-by-default sections; matchPath still auto-opens
      // the section when the current page lives inside it.
      selectedValue={defaultExpanded ? triggerValue : undefined}
      matchPath={matchPath}
      type="single"
      collapsible
      className="space-y-7.5 px-2.5"
      classNames={{
        item: 'h-8.5 px-2.5 text-sm font-normal text-foreground hover:text-link hover:bg-muted data-[selected=true]:bg-muted data-[selected=true]:text-foreground [&[data-selected=true]_svg]:opacity-100',
        subTrigger: 'text-xs font-normal text-muted-foreground hover:bg-transparent group [&_[data-slot="accordion-menu-sub-indicator"]]:hidden',
        subContent: 'ps-0',
        indicator: 'ms-auto flex items-center font-medium',
      }}
    >
      {config.map((item, index) => (
        <AccordionMenuSub key={index} value={sectionId}>
          <AccordionMenuSubTrigger value={triggerValue}>
            <span>{item.title}</span>
            <AccordionMenuIndicator>
              <Plus className="size-3.5 shrink-0 transition-transform duration-200 hidden group-data-[state=open]:block" />
              <Minus className="size-3.5 shrink-0 transition-transform duration-200 group-data-[state=open]:hidden" />
            </AccordionMenuIndicator>
          </AccordionMenuSubTrigger>

          <AccordionMenuSubContent type="single" collapsible parentValue={triggerValue}>
            {item.children
              ?.filter((child) => !child.hidden)
              .map((child, childIndex) => (
                <AccordionMenuItem key={childIndex} value={child.path || '#'}>
                  <Link
                    to={child.path || '#'}
                    onMouseEnter={() => child.path && prefetchRoute(child.path)}
                    onFocus={() => child.path && prefetchRoute(child.path)}
                  >
                    {child.icon && <child.icon />}
                    <span>{child.title}</span>
                    {child.badge && <Badge size="sm" variant="success" appearance="light">{child.badge}</Badge>}
                  </Link>
                </AccordionMenuItem>
              ))}
          </AccordionMenuSubContent>
        </AccordionMenuSub>
      ))}
    </AccordionMenu>
  );
}
